/**
 * `hec_page_title` resolution for page context v2 (COM-585, design D3).
 *
 * WHY THIS EXISTS. The rendered `<title>` is produced by `DeferSeo`, which
 * lives in `<body>` behind `Suspense`/`Await` (`app/seo/seo.tsx:94-112`) and is
 * hoisted into `<head>` by React 19 afterwards. The initial context script must
 * run BEFORE GTM and AB Tasty (`root.tsx:1669` vs `:1700`/`:1719` — the
 * contiguous inline-script run at the top of `<body>`), which is long before
 * that promise settles — so `document.title` is empty at the only moment we are
 * allowed to publish. Awaiting the deferred SEO chain to fix that
 * would pay TTFB on every document and partially undo a deliberate
 * optimization.
 *
 * So the title is resolved from data the loader already produced, using the
 * SAME composition the real tag uses — `renderTitle(titleTemplate, title)`
 * with the per-site defaults (`seo.server.ts:44-46`,
 * `generate-seo-tags.ts:412,778-798`). When a route's `seo` is a resolved
 * object this yields exactly the string that will render; when it is still a
 * promise (routes are allowed to defer it) we fall back to the site default
 * rather than block. That fallback is the documented, accepted divergence.
 *
 * Mirrors the `pageType` loader contract, which rejects a deferred
 * `Promise<string>` by a `typeof` guard for the same reason (`root.tsx:1227`).
 *
 * PURE: takes already-resolved values, so it runs identically on the server and
 * during a client navigation, and unit-tests under the node environment.
 */

/** The subset of a route's hoisted `seo` payload this reads. */
export interface PageTitleSeoInput {
  title?: unknown;
  titleTemplate?: unknown;
}

/**
 * The subset of the per-site SEO config this reads (`app/config/seo.config.ts`).
 * Both fields are optional because `SeoConfigType` declares them that way; every
 * configured site sets `defaultTitle` today, and a site that did not would
 * publish an empty title rather than an invented one.
 */
export interface PageTitleSiteDefaults {
  defaultTitle?: string;
  defaultTitleTemplate?: string;
}

/**
 * Apply a title template. Byte-identical to `renderTitle`
 * (`app/seo/utils/generate-seo-tags.ts:778-798`): an absent title yields
 * nothing, an absent template passes the title through, and the template
 * replaces the FIRST `%s` only — `String.prototype.replace` with a string
 * pattern, not a global regex.
 */
export function renderPageTitle(
  template: string | undefined,
  title: string | undefined,
): string | undefined {
  if (!title) return undefined;
  if (!template) return title;
  return template.replace("%s", title);
}

/**
 * `true` when a route's hoisted `seo` is usable right now — a plain object,
 * already resolved. A `Promise` (a route that defers its SEO) and any
 * non-object are rejected, exactly as the `pageType` scan rejects a deferred
 * page type.
 */
export function isResolvedSeo(seo: unknown): seo is PageTitleSeoInput {
  return (
    typeof seo === "object" &&
    seo !== null &&
    typeof (seo as { then?: unknown }).then !== "function"
  );
}

/** Minimal shape of a route match — `useMatches()` and router state both fit. */
export type SeoMatch = { data?: unknown };

/**
 * The deepest match carrying an `seo` payload, mirroring `matchedPageType`
 * (`page-type-selection.ts:31`). Route loaders hoist `seo` to the top level of
 * their payload — including the catch-all dispatcher, which destructures it out
 * of the page payload precisely so it sits there (`routes/$.tsx:1014,1027`).
 *
 * Returns the raw value; `resolvePageTitle` owns deciding whether it is usable,
 * because a route is allowed to defer it.
 */
export function matchedSeo(matches: readonly SeoMatch[]): unknown {
  for (let i = matches.length - 1; i >= 0; i--) {
    const data = matches[i]?.data as { seo?: unknown } | undefined;
    if (data?.seo != null) return data.seo;
  }
  return undefined;
}

/**
 * `matchedSeo` for raw router-subscriber state.
 *
 * Router state matches do NOT carry loader data — it lives in
 * `state.loaderData`, keyed by route id. Reading `match.data` here would find
 * nothing and silently fall back to the site default on every soft navigation.
 * This is the same re-keying `pageTypeFromRouterState`
 * (`page-type-selection.ts`) performs, and it exists for the same reason: the
 * SSR (`useMatches()`) and SPA (router state) shapes differ, so the mapping has
 * to be written down once rather than at each call site.
 */
export function seoFromRouterState(state: {
  matches: { route: { id: string } }[];
  loaderData: Record<string, unknown>;
}): unknown {
  return matchedSeo(
    state.matches.map(m => ({ data: state.loaderData[m.route.id] })),
  );
}

/**
 * Resolve the title to publish on a context message.
 *
 * Never returns an empty string: a context message with a blank title is
 * harder for a consumer to reason about than one carrying the site default,
 * and the field is non-optional in the contract.
 */
export function resolvePageTitle(
  seo: unknown,
  siteDefaults: PageTitleSiteDefaults,
): string {
  const resolved = isResolvedSeo(seo) ? seo : undefined;
  const routeTitle =
    typeof resolved?.title === "string" && resolved.title
      ? resolved.title
      : undefined;

  // No usable route title — a deferred `seo`, or a route that sets none. Return
  // the site default UNTEMPLATED.
  //
  // This deliberately diverges from `seo.server.ts:44-46`, which defaults
  // `title` to `defaultTitle` and then still applies `defaultTitleTemplate`.
  // Faithfully copying that here produces "FSA Store | FSA Store" on every
  // deferred-seo route — branding doubled, and a value no consumer would read
  // as a page title. The divergence only ever applies to the fallback path;
  // whenever a route title exists the composition below is exact.
  if (!routeTitle) return siteDefaults.defaultTitle ?? "";

  // ONLY the route's own template — never the site default as a fallback.
  //
  // This mirrors the real renderer exactly (`generate-seo-tags.ts:412`:
  // `renderTitle(seoInput?.titleTemplate, content)`), and the distinction is
  // load-bearing. Routes built with `getPageMeta` (cart, account, my-account)
  // always carry a `titleTemplate`, so they still template correctly. But PLP
  // and PDP use `seo.product.server.ts`, which sets `title` to the
  // ALREADY-COMPOSED string and deliberately sets no template — injecting the
  // site default there double-brands it.
  //
  // Measured on a deployed preview before this fix: `/specials` published
  // "Shop Specials | FSA Store | FSA Store" against a rendered `<title>` of
  // "Shop Specials | FSA Store".
  const template =
    typeof resolved?.titleTemplate === "string" && resolved.titleTemplate
      ? resolved.titleTemplate
      : undefined;

  return (
    renderPageTitle(template, routeTitle) ?? siteDefaults.defaultTitle ?? ""
  );
}
